import React, { useEffect } from "react";
import type { DisclaimerType } from "../../../types/api/moneyTransfer";
import DisplaySection from "../../MoneyTransfer/DisplaySection";
import { Spinner } from "../../Common/Spinner";
import { IconFont } from "../../Common/IconFont";
import useSendMoneyLimits from "../../../hooks/sendMoney/useSendMoneyLimits";
import { getApiErrorMessage } from "../../../hooks/useErrorModal";
import { ICON_FONT_CLASS, ICON_TYPE } from "../../../constants/iconfont";
import i18n from "../../../i18n/config";

interface SendMoneyLimitsContentProps {
  /** hoist the server-provided page title to a parent that renders it (e.g. a
   *  modal header). Called whenever the fetched title changes; omit where the
   *  title isn't shown (the wallet modal's inline expand has no header). */
  onTitle?: (title: string) => void;
  /** take over a failed fetch (e.g. open ApiErrorModal and close). Omit to
   *  show the server message inline instead. */
  onError?: (err: unknown) => void;
  hideSubtitle?: boolean;
}

/**
 * The limits body: per-wallet limit rows + footnotes, driven by
 * useSendMoneyLimits (by quote when one exists, else by country). Rendered by
 * the standalone SendMoneyLimitsModal and by the wallet modal's inline
 * "View limits" expand. Fetches on mount — the parent controls when this exists.
 *
 * The title lives on the same payload as the body, so this owns the fetch and
 * pushes the title up via onTitle rather than a second hook instance.
 */
const SendMoneyLimitsContent = ({
  onTitle,
  onError,
  hideSubtitle = false,
}: SendMoneyLimitsContentProps): React.ReactElement | null => {
  const { loading, sections, disclaimers, title, subTitle, hasError, error, fetchLimits } =
    useSendMoneyLimits();

  useEffect(() => {
    fetchLimits();
  }, [fetchLimits]);

  useEffect(() => {
    if (title) onTitle?.(title);
  }, [title, onTitle]);

  useEffect(() => {
    if (hasError) onError?.(error);
  }, [hasError, error, onError]);

  const renderDisclaimers = () => {
    if (!disclaimers.length) return null;
    return disclaimers.map((d: DisclaimerType) => (
      <p key={d.disclaimerId} className="font-size-sm disclaimer text-secondary">
        <sup>{d.superscript ?? d.disclaimerId} </sup>
        {d.text}
      </p>
    ));
  };

  const renderError = () => {
    // the parent surfaces it (ApiErrorModal) and closes
    if (onError) return null;
    return (
      <div className="flex-col gap-8">
        <p className="bold text-primary mb-0">
          <IconFont icon={ICON_FONT_CLASS.ALERT} type={ICON_TYPE.ALERT} />{" "}
          {i18n.t("COMMON.ERROR_MODAL_HEADING")}
        </p>
        <p className="text-secondary mb-0">{getApiErrorMessage(error)}</p>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex-col align-items-center justify-content-center gap-8">
        <Spinner show className="lds-ring--lg realtime-loading" />
      </div>
    );
  }
  if (hasError) return renderError();

  return (
    <>
      {!hideSubtitle && subTitle && (
        <p className="text-secondary spacing-bottom-small">{subTitle}</p>
      )}
      <DisplaySection variant="header-row" sections={sections} />
      {renderDisclaimers()}
    </>
  );
};

export default SendMoneyLimitsContent;